/**
 * Pagina ResetPasswordPage
 * Il Diario di Bordo - SaaS
 *
 * FIX: Aggiunta verifica token al caricamento pagina.
 * Prima mostrava il form senza verificare → dava l'impressione
 * che il link funzionasse anche dopo ore.
 * 
 * Ora:
 * 1. Al mount, chiama GET /auth/verify-reset-token
 * 2. Se token valido → mostra form + countdown tempo rimanente
 * 3. Se token scaduto/invalido → mostra errore + link per nuovo reset
 * 4. Al submit, gestisce errori specifici (token scaduto tra verifica e submit)
 */

import { useState, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import api from '../../services/api'
import { useNotification } from '../../contexts/NotificationContext'
import { Lock, Loader2, Eye, EyeOff, Clock, AlertTriangle, CheckCircle } from 'lucide-react'

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const { showSuccess, showError } = useNotification()
  const navigate = useNavigate()

  // FIX: Stati per verifica token
  const [verifying, setVerifying] = useState(true)
  const [tokenValid, setTokenValid] = useState(false)
  const [tokenError, setTokenError] = useState(null)
  const [tokenData, setTokenData] = useState(null)
  const [remainingMinutes, setRemainingMinutes] = useState(null)

  // FIX: Verifica token al caricamento pagina
  useEffect(() => {
    if (!token) {
      setVerifying(false)
      return
    }
    verifyToken()
  }, [token])

  // FIX: Countdown tempo rimanente
  useEffect(() => {
    if (!tokenData?.expiresAt) return

    const updateRemaining = () => {
      const now = new Date()
      const expires = new Date(tokenData.expiresAt)
      const remaining = Math.max(0, Math.floor((expires - now) / 60000))
      setRemainingMinutes(remaining)

      // Se scaduto durante la visualizzazione
      if (remaining <= 0) {
        setTokenValid(false)
        setTokenError('Il link per reimpostare la password è scaduto.')
      }
    }

    updateRemaining()
    const interval = setInterval(updateRemaining, 30000) // aggiorna ogni 30s
    return () => clearInterval(interval)
  }, [tokenData])

  const verifyToken = async () => {
    try {
      setVerifying(true)
      const response = await api.get(`/auth/verify-reset-token?token=${token}`)
      setTokenValid(true)
      setTokenData(response.data.data)
    } catch (err) {
      setTokenValid(false)
      const code = err.response?.data?.code
      if (code === 'INVALID_TOKEN') {
        setTokenError('Il link per reimpostare la password non è valido o è scaduto.')
      } else {
        setTokenError('Errore nella verifica del link. Riprova.')
      }
    } finally {
      setVerifying(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (password !== confirmPassword) { showError('Le password non corrispondono'); return }
    if (password.length < 8) { showError('Minimo 8 caratteri'); return }

    // Validazione extra frontend
    if (!/[A-Z]/.test(password)) { showError('La password deve contenere almeno una maiuscola'); return }
    if (!/[0-9]/.test(password)) { showError('La password deve contenere almeno un numero'); return }
    if (!/[^A-Za-z0-9]/.test(password)) { showError('La password deve contenere almeno un carattere speciale'); return }

    setLoading(true)
    try {
      await api.post('/auth/reset-password', { token, password })
      showSuccess('Password reimpostata con successo! Tutte le sessioni sono state disconnesse.')
      navigate('/login')
    } catch (error) {
      const code = error.response?.data?.code
      const message = error.response?.data?.message

      if (code === 'INVALID_TOKEN' || code === 'TOKEN_EXPIRED') {
        // Token scaduto tra la verifica iniziale e il submit
        setTokenValid(false)
        setTokenError(message || 'Il link è scaduto. Richiedi un nuovo link.')
      } else if (code === 'PASSWORD_RECENTLY_USED') {
        showError(message || 'Non puoi riutilizzare una password recente.')
      } else {
        showError(message || 'Errore durante il reset della password')
      }
    }
    setLoading(false)
  }

  // ─── Nessun token nell'URL ───
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-primary py-12 px-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
          <span className="text-4xl">❌</span>
          <h1 className="mt-4 text-xl font-bold text-gray-900 dark:text-white">Link non valido</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Il link per reimpostare la password non è valido.
          </p>
          <Link to="/forgot-password" className="mt-4 inline-block text-primary-600 font-medium hover:text-primary-700">
            Richiedi nuovo link
          </Link>
        </div>
      </div>
    )
  }

  // ─── Verifica in corso ───
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-primary py-12 px-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
          <Loader2 className="h-10 w-10 animate-spin text-primary-600 mx-auto" />
          <h1 className="mt-4 text-xl font-bold text-gray-900 dark:text-white">Verifica link...</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Stiamo verificando la validità del link.
          </p>
        </div>
      </div>
    )
  }

  // ─── Token non valido / scaduto ───
  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-primary py-12 px-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
          <div className="flex justify-center">
            <AlertTriangle className="h-12 w-12 text-amber-500" />
          </div>
          <h1 className="mt-4 text-xl font-bold text-gray-900 dark:text-white">Link scaduto</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {tokenError || 'Il link per reimpostare la password non è più valido.'}
          </p>
          <div className="mt-6 space-y-3">
            <Link 
              to="/forgot-password" 
              className="block w-full py-2.5 px-4 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              Richiedi nuovo link
            </Link>
            <Link 
              to="/login" 
              className="block text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              Torna al login
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // ─── Form reset password (token valido) ───
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-primary py-12 px-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
        <div className="text-center mb-6">
          <span className="text-4xl">🔐</span>
          <h1 className="mt-2 text-xl font-bold text-gray-900 dark:text-white">Nuova Password</h1>
          {tokenData?.email && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Per l'account {tokenData.email}
            </p>
          )}
        </div>

        {/* Countdown tempo rimanente */}
        {remainingMinutes !== null && remainingMinutes <= 15 && (
          <div className={`flex items-center gap-2 p-3 rounded-lg mb-4 text-sm ${
            remainingMinutes <= 5 
              ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400' 
              : 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400'
          }`}>
            <Clock className="h-4 w-4 flex-shrink-0" />
            <span>
              {remainingMinutes <= 0 
                ? 'Link scaduto' 
                : `Il link scade tra ${remainingMinutes} minut${remainingMinutes === 1 ? 'o' : 'i'}`
              }
            </span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nuova Password
            </label>
            <div className="relative">
              <input 
                type={showPassword ? 'text' : 'password'} 
                required 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                className="input pr-10" 
                placeholder="Minimo 8 caratteri" 
              />
              <button 
                type="button" 
                onClick={() => setShowPassword(!showPassword)} 
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {/* Indicatori requisiti password */}
            {password.length > 0 && (
              <div className="mt-2 space-y-1">
                <PasswordReq met={password.length >= 8} text="Almeno 8 caratteri" />
                <PasswordReq met={/[A-Z]/.test(password)} text="Almeno una maiuscola" />
                <PasswordReq met={/[0-9]/.test(password)} text="Almeno un numero" />
                <PasswordReq met={/[^A-Za-z0-9]/.test(password)} text="Almeno un carattere speciale" />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Conferma Password
            </label>
            <input 
              type={showPassword ? 'text' : 'password'} 
              required 
              value={confirmPassword} 
              onChange={(e) => setConfirmPassword(e.target.value)} 
              className="input" 
              placeholder="Ripeti la password" 
            />
            {confirmPassword && password !== confirmPassword && (
              <p className="text-xs text-red-500 mt-1">Le password non corrispondono</p>
            )}
          </div>

          <button 
            type="submit" 
            disabled={loading || !password || !confirmPassword || password !== confirmPassword}
            className="w-full py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Reimpostazione...
              </>
            ) : (
              <>
                <Lock className="h-4 w-4" />
                Reimposta Password
              </>
            )}
          </button>
        </form>

        <div className="mt-4 text-center">
          <Link to="/login" className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300">
            Torna al login
          </Link>
        </div>
      </div>
    </div>
  )
}

// ─── Componente helper per requisiti password ───
function PasswordReq({ met, text }) {
  return (
    <div className={`flex items-center gap-1.5 text-xs ${met ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
      {met ? <CheckCircle className="h-3 w-3" /> : <div className="h-3 w-3 rounded-full border border-current" />}
      <span>{text}</span>
    </div>
  )
}
